package es.cragma.fichajefacil.model

import es.cragma.fichajefacil.getCurrentTimeMillis

enum class UserRole(val displayName: String, val description: String) {
    GLOBAL_ADMIN(
        displayName = "Administrador Global",
        description = "Acceso global para administrar empresas y planes de suscripción"
    ),
    COMPANY_ADMIN(
        displayName = "Administrador Empresa",
        description = "Gestión completa de la empresa, empleados y configuraciones"
    ),
    HR(
        displayName = "Recursos Humanos",
        description = "Gestión de personal, altas/bajas, modificación de datos y fichajes"
    ),
    TEAM_LEADER(
        displayName = "Responsable de Equipo",
        description = "Supervisión de fichajes y estado de la jornada de su equipo"
    ),
    WORKER(
        displayName = "Trabajador",
        description = "Registro de fichajes, pausas y consulta de historial de jornada"
    );

    val isManagement: Boolean
        get() = this != WORKER
}

enum class IdentityDocumentType(val label: String) {
    DNI("DNI (Documento Nacional de Identidad)"),
    NIE("NIE (Número de Identidad de Extranjero)"),
    PASSPORT("Pasaporte"),
    OTHER("Otro Documento de Identidad")
}

enum class CompanyPlan(
    val displayName: String,
    val defaultLimit: Int,
    val monthlyPriceEuro: Int,
    val yearlyPriceEuro: Int
) {
    FREE("Gratuito", 5, 0, 0),
    BRONZE("Bronce", 10, 3, 20),
    SILVER("Plata", 30, 5, 45),
    GOLD("Oro", 50, 10, 100)
}

data class WorkplaceLocation(
    val id: String,
    val name: String, // e.g. "Sede Principal"
    val address: String = "",
    val latitude: Double = 0.0,
    val longitude: Double = 0.0,
    val radiusMeters: Int = 300 // Threshold radius in meters
)

data class Company(
    val id: String, // Unique 10-character alphanumeric ID (A-Z, 0-9)
    val name: String,
    val cif: String,
    val address: String = "",
    val city: String = "",
    val country: String = "España",
    val logoUrl: String? = null, // Company Logo URL / Preset Avatar
    val plan: CompanyPlan = CompanyPlan.FREE,
    val maxEmployees: Int = 5,
    val positions: List<String> = listOf(
        "Administrativo",
        "Desarrollo",
        "Diseño",
        "Gestión",
        "Comercial",
        "Técnico",
        "Operaciones"
    ),
    val departments: List<String> = listOf(
        "General",
        "Administración",
        "Técnico",
        "Ventas"
    ),
    val workplaces: List<WorkplaceLocation> = emptyList(),
    val allowUnverifiedLocationByDefault: Boolean = true,
    val createdAtMillis: Long = getCurrentTimeMillis()
)

data class User(
    val id: String,
    val firstName: String,
    val lastName: String,
    val email: String,
    val phone: String = "",
    val docType: IdentityDocumentType = IdentityDocumentType.DNI,
    val docNumber: String = "",
    val companyId: String?, // Null for Global Admin or Demo User
    val role: UserRole,
    val position: String = "Empleado",
    val department: String = "General",
    val isActive: Boolean = true, // Active status (false when "Dado de baja")
    val isDemoUser: Boolean = false, // True for demo@cragma.es read-only mode
    val allowUnverifiedLocationClockIn: Boolean = true,
    val createdAtMillis: Long = getCurrentTimeMillis()
) {
    val name: String
        get() = "$firstName $lastName".trim()
}

data class BreakRecord(
    val id: String,
    val startTimeMillis: Long,
    var startDateString: String = "",
    var endTimeMillis: Long? = null,
    var endDateString: String? = null
) {
    fun getDurationMillis(shiftEndTimeMillis: Long? = null): Long {
        val end = endTimeMillis
            ?: shiftEndTimeMillis
            ?: getCurrentTimeMillis()
        return (end - startTimeMillis).coerceAtLeast(0L)
    }

    val durationMillis: Long
        get() = getDurationMillis(null)

    val durationMinutes: Int
        get() = (durationMillis / 1000 / 60).toInt()
}

enum class ClockInStatus(val label: String) {
    NOT_STARTED("Sin iniciar"),
    WORKING("Trabajando"),
    ON_BREAK("En descanso"),
    FINISHED("Finalizado")
}

data class ClockInRecord(
    val id: String, // Unique 20-character identifier e.g. "20260324143215A8B9C1"
    val userId: String,
    val companyId: String,
    val userName: String,
    val userRole: UserRole,
    var startTimeMillis: Long,
    var startDateString: String = "",
    var endTimeMillis: Long? = null,
    var endDateString: String? = null,
    val breaks: MutableList<BreakRecord> = mutableListOf(),
    val isLocationVerified: Boolean = false,
    val startLocation: String? = null,
    var endLocation: String? = null,
    val startLatitude: Double? = null,
    val startLongitude: Double? = null,
    val startAltitude: Double? = null,
    var endLatitude: Double? = null,
    var endLongitude: Double? = null,
    var endAltitude: Double? = null,
    val startCountry: String = "España",
    var startCity: String = "",
    val startIpAddress: String = "",
    var endIpAddress: String? = null,
    val deviceModel: String = "",
    val batteryLevel: Int = 0,
    var endBatteryLevel: Int? = null,
    val language: String = "es",
    var isStartAtWorkplace: Boolean = false,
    var startWorkplaceName: String? = null,
    var isEndAtWorkplace: Boolean? = false,
    var endWorkplaceName: String? = null,
    val notes: String? = null,
    var isRectified: Boolean = false,
    var rectifiedBy: String? = null,
    var rectifiedAtMillis: Long? = null,
    var rectificationReason: String? = null
) {
    val totalElapsedMillis: Long
        get() = ((endTimeMillis ?: getCurrentTimeMillis()) - startTimeMillis).coerceAtLeast(0L)

    val totalBreakMillis: Long
        get() = breaks.sumOf { it.getDurationMillis(endTimeMillis) }

    val netWorkedMillis: Long
        get() = (totalElapsedMillis - totalBreakMillis).coerceAtLeast(0L)

    val currentStatus: ClockInStatus
        get() {
            if (endTimeMillis != null) return ClockInStatus.FINISHED
            if (breaks.any { it.endTimeMillis == null }) return ClockInStatus.ON_BREAK
            return ClockInStatus.WORKING
        }
}

fun formatDurationMillis(millis: Long): String {
    val totalSeconds = (millis / 1000).coerceAtLeast(0)
    val hours = totalSeconds / 3600
    val minutes = (totalSeconds % 3600) / 60
    val seconds = totalSeconds % 60

    return when {
        hours > 0 -> "${hours}h ${minutes}m ${seconds}s"
        minutes > 0 -> "${minutes}m ${seconds}s"
        else -> "${seconds}s"
    }
}

fun formatCompactDateTimeString(millis: Long): String {
    val totalSecs = millis / 1000
    val secs = (totalSecs % 60).toInt()
    val totalMins = totalSecs / 60
    val mins = (totalMins % 60).toInt()
    val totalHours = totalMins / 60
    val hours = (totalHours % 24).toInt()

    var days = totalHours / 24

    var year = 1970
    while (true) {
        val daysInYear = if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 366 else 365
        if (days >= daysInYear) {
            days -= daysInYear
            year++
        } else {
            break
        }
    }

    val isLeap = (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
    val daysInMonths = intArrayOf(31, if (isLeap) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
    var month = 0
    while (month < 12 && days >= daysInMonths[month]) {
        days -= daysInMonths[month]
        month++
    }
    val day = (days + 1).toInt()
    val monthNumber = month + 1

    val dd = day.toString().padStart(2, '0')
    val mm = monthNumber.toString().padStart(2, '0')
    val yyyy = year.toString()
    val hh = hours.toString().padStart(2, '0')
    val min = mins.toString().padStart(2, '0')
    val ss = secs.toString().padStart(2, '0')

    return "$yyyy$mm$dd$hh$min$ss"
}

fun formatDateTimeString(millis: Long): String {
    val totalSecs = millis / 1000
    val secs = (totalSecs % 60).toInt()
    val totalMins = totalSecs / 60
    val mins = (totalMins % 60).toInt()
    val totalHours = totalMins / 60
    val hours = (totalHours % 24).toInt()

    var days = totalHours / 24

    var year = 1970
    while (true) {
        val daysInYear = if ((year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)) 366 else 365
        if (days >= daysInYear) {
            days -= daysInYear
            year++
        } else {
            break
        }
    }

    val isLeap = (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
    val daysInMonths = intArrayOf(31, if (isLeap) 29 else 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31)
    var month = 0
    while (month < 12 && days >= daysInMonths[month]) {
        days -= daysInMonths[month]
        month++
    }
    val day = (days + 1).toInt()
    val monthNumber = month + 1

    val dd = day.toString().padStart(2, '0')
    val mm = monthNumber.toString().padStart(2, '0')
    val yyyy = year.toString()
    val hh = hours.toString().padStart(2, '0')
    val min = mins.toString().padStart(2, '0')
    val ss = secs.toString().padStart(2, '0')

    return "$dd/$mm/$yyyy $hh:$min:$ss"
}

fun generateUnique20CharClockInId(millis: Long): String {
    val datePrefix = formatCompactDateTimeString(millis) // 14 chars YYYYMMDDHHmmss
    val chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789"
    val randomSuffix = (1..6).map { chars.random() }.joinToString("") // 6 chars
    return datePrefix + randomSuffix // Exactly 20 characters
}

enum class LoginAccessMode(val label: String, val description: String) {
    WORKER("Modo Trabajador", "Fichar entrada/salida y registrar pausas de jornada"),
    MANAGEMENT("Modo Administración", "Gestión de trabajadores, fichajes y panel de control")
}

data class UserShiftStatus(
    val user: User,
    val status: ClockInStatus,
    val activeClockIn: ClockInRecord?,
    val lastClockIn: ClockInRecord?,
    val lastShiftEndedText: String
)

data class SecurityAuditEntry(
    val id: String,
    val companyId: String,
    val action: String,
    val performedBy: String,
    val timestampMillis: Long = getCurrentTimeMillis(),
    val details: String
)
